/**
 * PrivacyChoicesControl — the compact "Help us understand visits" control (§3).
 *
 * Contract:
 *   • Popover-based, low-visual-prominence, ordinary secondary-link styling.
 *   • Never modal-on-load, never blocking, never warning-colored.
 *   • Truthfully shows On / Off / Unavailable (§18).
 *   • Uses the canonical `privacyChoices` preference store (§19).
 *   • Provides an ordinary link to the full Privacy Policy (§3, §15).
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  getEffectiveState,
  setMeasurementEnabled,
  subscribePrivacyChanges,
} from "@/lib/privacyChoices";

export function PrivacyChoicesControl({ className = "", triggerClassName = "" }) {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") {
      return { enabled: true, durable: true, explicit: false, choice: null };
    }
    return getEffectiveState();
  });

  useEffect(() => {
    const unsub = subscribePrivacyChanges((next) => setState(next));
    return () => { try { unsub(); } catch { /* noop */ } };
  }, []);

  const handleToggle = useCallback((next) => {
    setState(setMeasurementEnabled(next));
  }, []);

  const label = state.durable
    ? (state.enabled ? "On" : "Off")
    : "Unavailable in this browser";

  return (
    <Popover>
      <PopoverTrigger
        className={`text-inherit underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-current ${triggerClassName}`}
        data-testid="privacy-choices-trigger"
        aria-label="Privacy Choices"
      >
        Privacy Choices
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className={`w-72 p-4 space-y-3 text-sm ${className}`}
        data-testid="privacy-choices-panel"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-foreground" data-testid="privacy-choices-title">
              Help us understand visits
            </div>
            <div
              className="text-xs text-muted-foreground mt-0.5"
              data-testid="privacy-choices-state"
              data-privacy-state={state.enabled ? "on" : "off"}
              data-privacy-durable={state.durable ? "true" : "false"}
            >
              {label}
            </div>
          </div>
          <Switch
            checked={state.enabled && state.durable}
            disabled={!state.durable}
            onCheckedChange={handleToggle}
            data-testid="privacy-choices-toggle"
            aria-label="Help us understand visits"
          />
        </div>
        <p className="text-xs text-muted-foreground leading-5" data-testid="privacy-choices-copy">
          Keeps basic visit and campaign info on this browser so we can see
          what brings people in. Turn it off anytime.
        </p>
        {!state.durable && (
          <p className="text-[11px] text-muted-foreground leading-4" data-testid="privacy-choices-storage-note">
            Your browser is limiting storage, so this choice can&rsquo;t be
            remembered here. Visits are minimally attributed for this
            session only.
          </p>
        )}
        <div className="text-xs">
          <Link
            to="/privacy"
            className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
            data-testid="privacy-choices-policy-link"
          >
            Read the full Privacy policy
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default PrivacyChoicesControl;
